import React, { useState } from "react";
import { Send, CheckCircle2, ArrowRight, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import { submitServiceRequest, checkEmailExists } from "../../api";

const SERVICE_TYPES = [
  { value: "instant_pnl", label: "Instant PnL" },
  { value: "1031_exchange_accounting", label: "1031 Exchange Accounting" },
  { value: "accounting_consultation", label: "Accounting Consultation" },
  { value: "tax_preparation", label: "Tax Preparation" },
  { value: "bookkeeping", label: "Bookkeeping" },
  { value: "audit_support", label: "Audit Support" },
  { value: "entity_formation", label: "Entity Formation" },
  { value: "other", label: "Other" },
];

const inputCls =
  "w-full bg-black border border-white/20 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500";

const formatDetail = (detail) => {
  if (detail == null) return "Please check the form and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
};

export const ServiceRequestForm = ({ onAuthed, currentUser, onDone }) => {
  const [form, setForm] = useState({
    full_name: (currentUser && currentUser.full_name) || "",
    email: (currentUser && currentUser.email) || "",
    phone_number: "", service_type: "instant_pnl", description: "", password: "",
  });
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onEmailBlur = async () => {
    const email = form.email.trim();
    if (!email || !email.includes("@")) return;
    try {
      const res = await checkEmailExists(email);
      setEmailExists(res.exists);
      setEmailChecked(true);
    } catch {
      setEmailExists(false);
      setEmailChecked(true);
    }
  };

  const needsPassword = !currentUser && emailChecked && !emailExists;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form, phone_number: form.phone_number || null };
      if (!payload.password) delete payload.password;
      const res = await submitServiceRequest(payload);
      setResult(res);
    } catch (err) {
      toast.error("Submission failed", {
        description: formatDetail(err.response && err.response.data && err.response.data.detail),
      });
    }
    setBusy(false);
  };

  if (result)
    return (
      <div className="glass-card rounded-md border border-emerald-500/30 p-8 space-y-5" data-testid="sr-confirmation">
        <div className="flex items-center space-x-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-400" />
          <h3 className="text-lg font-black text-white">Request received</h3>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Your request <span className="font-mono text-zinc-300">{result.request_id}</span> has been submitted.{" "}
          {currentUser
            ? "It's been linked to your account — you can track its progress and attach documents from My Requests."
            : result.account_status === "created_new"
            ? "An account has been created for you — use the button below to open your client portal now, or sign in anytime with your email and password."
            : "We found your existing account and linked this request to your profile — sign in with your email and password to track its progress."}
        </p>
        {currentUser && (
          <button
            data-testid="sr-back-to-requests-btn"
            onClick={onDone}
            className="w-full bg-white hover:bg-zinc-200 text-black text-xs font-bold py-2.5 rounded-md transition-colors flex items-center justify-center space-x-1.5"
          >
            <span>Back to My Requests</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        {result.access_token && (
          <button
            data-testid="sr-portal-btn"
            onClick={() => onAuthed({ access_token: result.access_token, user: result.user })}
            className="w-full bg-white hover:bg-zinc-200 text-black text-xs font-bold py-2.5 rounded-md transition-colors flex items-center justify-center space-x-1.5"
          >
            <span>Go to your client portal</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        <button data-testid="sr-another-btn" onClick={() => setResult(null)} className="text-xs text-zinc-400 hover:text-white transition-colors">
          Submit another request
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="glass-card rounded-md border border-white/10 p-8 space-y-4" data-testid="service-request-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="sr-name-input" required minLength={2} placeholder="Full name" value={form.full_name} onChange={set("full_name")} disabled={!!currentUser} className={`${inputCls} ${currentUser ? "opacity-60 cursor-not-allowed" : ""}`} />
        <input data-testid="sr-phone-input" placeholder="Phone (optional)" value={form.phone_number} onChange={set("phone_number")} className={inputCls} />
      </div>
      <div className="space-y-1.5">
        <input data-testid="sr-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} onBlur={onEmailBlur} disabled={!!currentUser} className={`${inputCls} ${currentUser ? "opacity-60 cursor-not-allowed" : ""}`} />
        {emailExists && !currentUser && (
          <div className="flex items-start space-x-2 text-[11px] text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-md px-3 py-2" data-testid="sr-existing-notice">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>We found an existing account for this email. We'll link this request to your profile — sign in with your password to track it.</span>
          </div>
        )}
      </div>
      {needsPassword && (
        <div className="space-y-1.5">
          <div className="relative">
            <Lock className="w-4 h-4 text-zinc-500 absolute left-3 top-3" />
            <input
              data-testid="sr-password-input"
              required
              type="password"
              minLength={8}
              placeholder="Create a portal password (min 8 characters)"
              value={form.password}
              onChange={set("password")}
              className={`${inputCls} pl-10`}
            />
          </div>
          <p className="text-[10px] text-zinc-600">New here — this password lets you sign in and track your request.</p>
        </div>
      )}
      <select data-testid="sr-type-select" value={form.service_type} onChange={set("service_type")} className={inputCls}>
        {SERVICE_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      <textarea data-testid="sr-desc-input" required minLength={5} rows={4} placeholder="Tell us what you need — e.g. assistance with corporate tax structuring…" value={form.description} onChange={set("description")} className={inputCls} />
      <button
        type="submit"
        disabled={busy}
        data-testid="sr-submit-btn"
        className="w-full bg-white hover:bg-zinc-200 disabled:opacity-50 text-black font-bold py-3 rounded-md text-sm transition-colors flex items-center justify-center space-x-2"
      >
        <Send className="w-4 h-4" />
        <span>{busy ? "Submitting…" : "Submit Service Request"}</span>
      </button>
      <p className="text-[10px] text-zinc-600 text-center">
        {currentUser ? "This request will be linked to your account automatically." : "We'll set up your client portal automatically when you submit."}
      </p>
    </form>
  );
};
